// Reine Logik rund um Mail-Anhänge über eine Konversation hinweg — bewusst ohne
// Netz/DOM-Imports, damit sie im Vitest-„node"-Runner testbar ist (graphMailService
// selbst zieht browser-lastige Utils und darf hier nicht importiert werden).

/**
 * Aus den Nachrichten einer Konversation die Anhang-Quellen ableiten: welche
 * Message-ids Anhänge tragen (fürs Nachladen) + aggregiertes `hasAttachments`.
 *
 * Behebt die Thread-Lücke: Der angezeigte Repräsentant (neueste Nachricht) hat
 * oft KEINEN Anhang, obwohl eine frühere Thread-Nachricht einen trägt. Vorher
 * wurden Anhänge nur für die neueste Nachricht geladen → sie fehlten scheinbar.
 */
export function conversationAttachmentSources(
  items: Array<{ id: string; hasAttachments?: boolean }>,
): { hasAttachments: boolean; attachmentMessageIds: string[] } {
  const attachmentMessageIds = items.filter((m) => m.hasAttachments).map((m) => m.id);
  return { hasAttachments: attachmentMessageIds.length > 0, attachmentMessageIds };
}

/**
 * Anhänge aus mehreren Nachrichten zusammenführen, je (messageId,id) dedupliziert.
 * Attachment-ids sind nur PRO Nachricht eindeutig — der zusammengesetzte Schlüssel
 * verhindert Kollisionen über Thread-Nachrichten hinweg.
 */
export function dedupeAttachments<T extends { id: string; messageId: string }>(
  list: T[],
): T[] {
  const seen = new Set<string>();
  const out: T[] = [];
  for (const a of list) {
    const key = attachmentKey(a);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(a);
  }
  return out;
}

/**
 * Stabiler Schlüssel eines Anhangs über eine Konversation hinweg — `id` ist nur
 * PRO Nachricht eindeutig, daher der zusammengesetzte `messageId:id`. Genutzt
 * für Dedupe, Auswahl-Sets und Save-/Download-State.
 */
export function attachmentKey(a: { id: string; messageId: string }): string {
  return `${a.messageId}:${a.id}`;
}

/**
 * Anhang-Metadaten (FE-Shape, `id`) in die Import-Wire-Shape (`attachmentId`)
 * übersetzen — für `POST /mail/attachments/import` (Übernahme als Kundendokument).
 */
export function toImportItems<T extends { id: string; messageId: string }>(
  list: T[],
): Array<{ messageId: string; attachmentId: string }> {
  return list.map((a) => ({ messageId: a.messageId, attachmentId: a.id }));
}
